import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { errorMessage } from '../lib/api'

/**
 * Optimistic actions with undo.
 *
 * `schedule()` applies the change to the UI immediately and holds the server
 * call for UNDO_MS. Undo inside that window just reverts the UI — the server
 * never heard about it, so UI and server state can't disagree. After the window
 * the call is committed; if it fails the UI is reverted and an error toast
 * explains why.
 *
 * Pending commits are flushed on `pagehide` (requests use keepalive) so closing
 * the tab doesn't silently drop work someone thinks they did.
 */
const UNDO_MS = 5_000

export interface DeferredAction {
  /** Past tense, shown in the toast: "Resolved #1042". */
  label: string
  apply: () => void
  revert: () => void
  commit: () => Promise<unknown>
  onCommitted?: () => void
}

type Toast = { id: number; text: string; tone: 'info' | 'error'; undo?: () => void }

interface ToastApi {
  schedule: (a: DeferredAction) => void
  /** Undo the most recent still-pending action. Returns false if there was none. */
  undoLast: () => boolean
  toast: (text: string, tone?: Toast['tone']) => void
}

const Ctx = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const c = useContext(Ctx)
  if (!c) throw new Error('useToast outside <ToastProvider>')
  return c
}

let seq = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const pending = useRef(new Map<number, { action: DeferredAction; timer: number }>())

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), [])

  const toast = useCallback(
    (text: string, tone: Toast['tone'] = 'info') => {
      const id = ++seq
      setToasts((t) => [...t, { id, text, tone }])
      window.setTimeout(() => dismiss(id), tone === 'error' ? 8_000 : 4_000)
    },
    [dismiss],
  )

  const commit = useCallback(
    (id: number) => {
      const p = pending.current.get(id)
      if (!p) return
      pending.current.delete(id)
      window.clearTimeout(p.timer)
      dismiss(id)
      p.action
        .commit()
        .then(() => p.action.onCommitted?.())
        .catch((e: unknown) => {
          p.action.revert()
          toast(`Couldn't save "${p.action.label}" — undone. ${errorMessage(e)}`, 'error')
        })
    },
    [dismiss, toast],
  )

  const undo = useCallback(
    (id: number) => {
      const p = pending.current.get(id)
      if (!p) return
      pending.current.delete(id)
      window.clearTimeout(p.timer)
      p.action.revert()
      dismiss(id)
    },
    [dismiss],
  )

  const schedule = useCallback(
    (action: DeferredAction) => {
      const id = ++seq
      action.apply()
      const timer = window.setTimeout(() => commit(id), UNDO_MS)
      pending.current.set(id, { action, timer })
      setToasts((t) => [...t.slice(-3), { id, text: action.label, tone: 'info', undo: () => undo(id) }])
    },
    [commit, undo],
  )

  const undoLast = useCallback(() => {
    const ids = [...pending.current.keys()]
    const last = ids[ids.length - 1]
    if (last === undefined) return false
    undo(last)
    return true
  }, [undo])

  useEffect(() => {
    const flush = () => [...pending.current.keys()].forEach(commit)
    window.addEventListener('pagehide', flush)
    return () => window.removeEventListener('pagehide', flush)
  }, [commit])

  const value = useMemo(() => ({ schedule, undoLast, toast }), [schedule, undoLast, toast])

  return (
    <Ctx.Provider value={value}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex -translate-x-1/2 flex-col items-center gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm shadow-lg ${
              t.tone === 'error' ? 'bg-critical-fg text-white' : 'bg-slate-900 text-white'
            }`}
          >
            {t.tone === 'error' && <span aria-hidden>●</span>}
            <span>{t.text}</span>
            {t.undo && (
              <button className="font-semibold text-blue-300 hover:text-blue-200" onClick={t.undo}>
                Undo
              </button>
            )}
            <button aria-label="Dismiss" className="text-slate-400 hover:text-white" onClick={() => dismiss(t.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}
